import { Helmet } from "react-helmet-async";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  absoluteUrl,
  organizationSchema,
} from "@/lib/seo-config";

/**
 * Drop-in SEO component. Place once near the top of any page.
 *
 * Props:
 * - title:       Page-specific title (without site suffix).
 * - description: <= 160 chars summary for SERP + social cards.
 * - path:        Site-relative URL of THIS page (e.g. "/services").
 * - image:       Absolute image URL for og:image / twitter:image.
 * - type:        OG type ("website" | "article" | "profile"). Default "website".
 * - noIndex:     Hide from search engines (admin pages, drafts).
 * - schemas:     Array of additional JSON-LD objects to inject.
 * - article:     If provided, builds an Article schema. Shape:
 *                { headline, description, image, datePublished, dateModified, author, slug, category }
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noIndex = false,
  schemas = [],
  article,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — One of Bermuda's Most Trusted Practices for Clinical Massage Therapy`;
  const canonical = absoluteUrl(path);
  const ogImage = image || DEFAULT_OG_IMAGE;

  const allSchemas = [organizationSchema(), ...schemas];

  if (article) {
    allSchemas.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline || title,
      description: article.description || description,
      image: article.image || ogImage,
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      author: {
        "@type": "Organization",
        name: article.author || `${SITE_NAME} Clinical Massage Therapy Team`,
        url: absoluteUrl("/team"),
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/rskp9grq_ashlan_logo.png",
        },
      },
      articleSection: article.category,
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content="en_BM" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {allSchemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          // Helmet serialises this to inline JSON safely.
        >
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
